import styled from 'styled-components';
import { Icon, Input, Text, Title, message, Button } from 'components';
import {
  getSupraLockboxData,
  unassignSupraLockbox,
} from 'api/SupraLockboxController';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts';
import { useDebounce } from 'hooks';
import { useHistory } from 'react-router-dom';
import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';

import { featureEnabled } from 'utils';
import { Flex, FlexAlign, Section, Header } from '../styles';
import LockboxTable from './LockboxTable';
import AddLockboxModal from './AddLockboxModal';
import MLSUserPageContext from '../MLSUserPageContext';

const LockboxSection = ({ sectionRef }: { sectionRef: React.Ref<any> }) => {
  const { auth } = useContext(AuthContext);
  const {
    user,
    lockboxes,
    setLockboxes,
    lockboxesCount,
    setLockboxesCount,
    subscriptions,
  } = useContext(MLSUserPageContext);
  const [loadingData, setLoadingData] = useState(true);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const debounceQuery = useDebounce(query, 500);

  const fetchLockboxData = useCallback(async () => {
    setLoadingData(true);
    try {
      if (!user?.id) return;

      const { lockboxes: _lockboxes, lockboxCount: count } =
        await getSupraLockboxData({
          ownerId: user?.id,
          ownerType: 'user',
          assigneeId: user?.id,
          assigneeType: 'user',
          searchText: debounceQuery,
          page,
          includeMlsNumbers: true,
          includeOfficePhoneNumbers: true,
          requireOwnerAsMLS: true,
          filterBySupraOwnerMapping: false,
        });
      setLockboxesCount(count);
      setLockboxes(_lockboxes);
    } catch (err) {
      message.error('Something went wrong');
    }
    setLoadingData(false);
  }, [user?.id, debounceQuery, page]);

  const unassignLockbox = async (serialNumber: string, userId: string) => {
    try {
      await unassignSupraLockbox(serialNumber, userId);
      const filteredData = lockboxes.filter(
        lockbox => lockbox.serialNumber !== serialNumber,
      );

      setLockboxes(filteredData);
      setLockboxesCount(lockboxesCount - 1);
      message.success('Lockbox unassigned');

      if (filteredData.length === 0) {
        fetchLockboxData();
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  const isSubscriptionStatusActive =
    subscriptions?.supra?.status === SupraSystemSubscriptionStatus.subscribed;

  const isLimitedWithAssignLockboxDisabled =
    subscriptions?.supra?.type === SupraSystemSubscriptionType.LIMITED &&
    !subscriptions?.supra?.assignLockboxesReleaseShackles;

  const canAssignLockboxesToUser =
    (!subscriptions.supra && user?.org.features.includes('SUPRA_MVP')) ||
    (subscriptions.supra.type !== SupraSystemSubscriptionType.MANAGED_ACCESS &&
      !isLimitedWithAssignLockboxDisabled);

  useEffect(() => {
    fetchLockboxData();
  }, [fetchLockboxData, user?.id, debounceQuery, page]);

  const history = useHistory();

  return (
    <Section
      id="supra-lockbox-section"
      ref={sectionRef}
      data-cy={'lockbox-section'}
    >
      <Header>
        <StyledTitle level={3}>Lockboxes</StyledTitle>
        <Flex>
          <FlexAlign>
            <Text type="secondary" style={{ marginRight: '10px' }}>
              {lockboxes.length} of {lockboxesCount} lockboxes
            </Text>
            <Input
              placeholder="Search lockboxes"
              value={query}
              onChange={e => setQuery(e.target.value)}
              prefix={<Icon type="search" color="gray7" />}
            />
            <Button
              style={{ marginLeft: '10px' }}
              onClick={() =>
                history.push(`/mls/user/${user?.id}/lockboxactivity`)
              }
            >
              View Activity
            </Button>
            {featureEnabled('supraAutoCoopFeature') &&
            user?.coopType === SupraCoopType.AUTO_COOP &&
            user?.coopRelationshipType === 'oneWay' ? null : (
              <AddLockboxModal
                isLimitedWithAssignLockboxDisabled={
                  isLimitedWithAssignLockboxDisabled
                }
                canAssignLockboxesToUser={canAssignLockboxesToUser}
                fetchLockboxData={fetchLockboxData}
                mlsStaffOrgId={auth?.org?.id}
                user={user}
              />
            )}
          </FlexAlign>
        </Flex>
      </Header>
      <LockboxTable
        isLimitedWithAssignLockboxDisabled={isLimitedWithAssignLockboxDisabled}
        canAssignLockboxesToUser={canAssignLockboxesToUser}
        unassignLockbox={unassignLockbox}
        totalLockboxCount={lockboxesCount}
        page={page}
        setPage={setPage}
        lockboxData={lockboxes}
        loadingData={loadingData}
        user={user}
      />
    </Section>
  );
};

const StyledTitle = styled(Title)`
  && {
    margin: 0;
    margin-left: 9px;
    font-weight: 500;
  }
`;

export default LockboxSection;
